/** Local source navigation over server-resolved evidence. No fetch, inference or profile reconstruction. */
import { createContext, useContext, useId, useState, type ReactNode } from 'react'
import type { ShopifyResultQuote } from '@testfeed/shopify-contracts'
import { ServerResultSourceModal } from './ServerResultSourceModal'

type Exchange = NonNullable<ShopifyResultQuote['originalExchange']>
type Segments = Exchange['answers'][number]['segments']
type SourceSelection = { id: string; segments?: Segments; exchange: Exchange }
const SourceNavigation = createContext<{ modalId: string; select: (id: string, segments?: Segments) => void } | null>(
  null,
)

export function ServerResultSource({ exchange, children }: { exchange: Exchange; children: ReactNode }) {
  const modalId = useId()
  const [selection, setSelection] = useState<SourceSelection | null>(null)
  const selected = selectedSourceAnswer(exchange, selection)
  const select = (id: string, segments?: Segments) => setSelection({ id, segments, exchange })
  return (
    <SourceNavigation.Provider value={{ modalId, select }}>
      {children}
      <ServerResultSourceModal id={modalId} exchange={exchange} selected={selected} />
    </SourceNavigation.Provider>
  )
}

function selectedSourceAnswer(exchange: Exchange, selection: SourceSelection | null) {
  // A refreshed server view must never reuse a highlight selection from a different source object.
  if (!selection || selection.exchange !== exchange) return null
  const original = exchange.answers.find((answer) => answer.id === selection.id)
  if (!original) return null
  if (selection.segments) return { ...original, segments: selection.segments }
  return original
}

export function ServerResultSourceLink({
  answerId,
  segments,
  children,
}: {
  answerId: string
  segments?: Segments
  children: ReactNode
}) {
  const navigation = useContext(SourceNavigation)
  if (!navigation) throw new Error('Result source link requires its source navigation owner.')
  return (
    <s-link commandFor={navigation.modalId} onClick={() => navigation.select(answerId, segments)}>
      {children}
    </s-link>
  )
}
